import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { BUSINESSES, RIVAL, rivalPriceFor, rivalStealChance } from "@/data/gameData";
import { useGame } from "@/context/GameContext";
import { AlertCircle, TrendingUp } from "lucide-react";
import { Sound } from "@/lib/sound";

const CustomerCard = ({ job, onStart, onDecline }) => {
  const biz = BUSINESSES[job.businessId];
  const { applyBoosts, state } = useGame();
  const [range] = useState(biz.priceRange);
  const [price, setPrice] = useState(job.suggestedPrice);
  const sliderValue = useMemo(() => [price], [price]);
  const rivalPrice = rivalPriceFor(job);
  const stealChance = rivalStealChance({ price, rivalPrice, playerRep: state.reputation, rivalRep: state.rival.reputation });
  const risk = stealChance >= 0.45 ? "high" : stealChance >= 0.2 ? "medium" : "low";

  const { price: adjustedPrice, cost: adjustedCost } = applyBoosts(
    biz.id,
    price,
    biz.baseCost
  );
  const profit = adjustedPrice - adjustedCost;

  const isTooCheap = price < job.suggestedPrice - 4;
  const isTooExpensive = price > job.suggestedPrice + 5;

  return (
    <div
      className={`bg-white rounded-3xl p-5 border-2 ${biz.borderColor} card-3d`}
      data-testid={`customer-card-${job.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`text-4xl ${biz.lightBg} rounded-2xl p-2 border-2 ${biz.borderColor}`}>
          {biz.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-display text-lg font-bold text-slate-900">{job.name}</h3>
            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${biz.lightBg} ${biz.textColor}`}>
              {biz.name}
            </span>
          </div>
          <p className="text-sm text-slate-600 font-medium">{job.pet}</p>
          {job.fromFlyer && (
            <span className="inline-block mt-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300" data-testid="flyer-customer-badge">
              📣 Saw your flyer!
            </span>
          )}
        </div>
      </div>

      <div className="bg-slate-50 border-2 border-slate-100 rounded-2xl p-3 mb-4">
        <p className="text-sm text-slate-700 italic leading-relaxed">&ldquo;{job.note}&rdquo;</p>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-black uppercase tracking-wider text-slate-500">
            Set your price
          </span>
          <span className={`font-display text-2xl font-bold ${biz.textColor}`} data-testid="price-value">
            ${price}
          </span>
        </div>

        <Slider
          value={sliderValue}
          onValueChange={(v) => setPrice(v[0])}
          min={range[0]}
          max={range[1]}
          step={1}
          data-testid="price-slider"
        />

        <div className="flex items-center justify-between text-xs text-slate-500 font-semibold">
          <span>${range[0]} (low)</span>
          <span>Suggested: ${job.suggestedPrice}</span>
          <span>${range[1]} (high)</span>
        </div>

        <div
          className={`flex items-center justify-between gap-2 text-xs font-bold rounded-xl px-3 py-2 border ${
            risk === "high" ? "bg-rose-50 border-rose-300 text-rose-800" : risk === "medium" ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-600"
          }`}
          data-testid="rival-compare"
        >
          <span>🧢 {RIVAL.name} charges <b>${rivalPrice}</b> · ★{state.rival.reputation.toFixed(1)}</span>
          <span className="uppercase text-[10px] font-black" data-testid="rival-risk">
            {risk === "high" ? "⚠ Might pick Riley!" : risk === "medium" ? "Hmm… risky" : "✓ You're the pick"}
          </span>
        </div>

        {isTooCheap && (
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 rounded-xl px-3 py-2 border border-amber-200">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>Too cheap! You'll barely make a profit.</span>
          </div>
        )}
        {isTooExpensive && (
          <div className="flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 rounded-xl px-3 py-2 border border-rose-200">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>Pricey! Customer might not be happy with the price.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl py-2">
          <div className="text-[10px] font-black uppercase text-emerald-700">Earn</div>
          <div className="font-display font-bold text-emerald-800">${adjustedPrice}</div>
        </div>
        <div className="bg-rose-50 border-2 border-rose-200 rounded-xl py-2">
          <div className="text-[10px] font-black uppercase text-rose-700">Cost</div>
          <div className="font-display font-bold text-rose-800">-${adjustedCost}</div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl py-2">
          <div className="text-[10px] font-black uppercase text-blue-700">Profit</div>
          <div className="font-display font-bold text-blue-800 flex items-center justify-center gap-0.5">
            <TrendingUp className="w-3 h-3" />${profit}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={() => { Sound.tap(); onDecline(job.businessId, job.id); }}
          variant="outline"
          className="flex-1 border-2 border-slate-300 rounded-2xl font-heading font-bold text-slate-700 hover:bg-slate-100 py-5"
          data-testid={`decline-job-${job.id}`}
        >
          Skip
        </Button>
        <Button
          onClick={() => { Sound.whoosh(); onStart(job, { price, cost: biz.baseCost, isTooCheap, isTooExpensive, rivalPrice, stealChance }); }}
          className={`flex-1 ${biz.solidBg} ${biz.hoverBg} text-white font-black font-heading rounded-2xl border-2 border-black/10 btn-3d py-5`}
          data-testid={`accept-job-${job.id}`}
        >
          Accept Job →
        </Button>
      </div>
    </div>
  );
};

export default CustomerCard;
